import { getSearchMessagingParticipantsMockHandler } from "@vega-api/generated/coinvest-deal-messaging-participants/coinvest-deal-messaging-participants.msw";
import {
  getCreateConversationMockHandler,
  getGetConversationMockHandler,
  getGetConversationUnreadSummaryMockHandler,
  getListConversationsMockHandler,
} from "@vega-api/generated/messaging-conversations-v1/messaging-conversations-v1.msw";
import {
  getListMessagesMockHandler,
  getSendMessageMockHandler,
} from "@vega-api/generated/messaging-messages-v1/messaging-messages-v1.msw";
import { getMarkConversationReadMockHandler } from "@vega-api/generated/messaging-participants-v1/messaging-participants-v1.msw";
import { type ConversationRequest } from "@vega-api/generated/types/conversationRequest";
import { type ConversationResponse } from "@vega-api/generated/types/conversationResponse";
import { ConversationStatus } from "@vega-api/generated/types/conversationStatus";
import { MessageContentType } from "@vega-api/generated/types/messageContentType";
import { type MessageRequest } from "@vega-api/generated/types/messageRequest";
import { type MessageResponse } from "@vega-api/generated/types/messageResponse";
import { type MessageWithMetaResponse } from "@vega-api/generated/types/messageWithMetaResponse";
import { type MessagingParticipant } from "@vega-api/generated/types/messagingParticipant";
import { type SenderInfo } from "@vega-api/generated/types/senderInfo";
import { SenderType } from "@vega-api/generated/types/senderType";

// Must match getCustomProfileHandler's user id and the pinned deal fixtures in
// coinvestHandlers.ts so "own" messages align and deal links resolve.
const CURRENT_USER_ID = "user-1";
const SERIES_A = {
  dealId: "123e4567-e89b-12d3-a456-426614170001",
  dealOpportunityId: "223e4567-e89b-12d3-a456-426614170003",
  dealName: "Series A Investment",
};
const PROJECT_TITAN = {
  dealId: "323e4567-e89b-12d3-a456-426614170004",
  dealOpportunityId: "523e4567-e89b-12d3-a456-426614170005",
  dealName: "Project Titan",
};

const VEGA_PENSION_FUND_CONTACT = "9c1e4567-e89b-12d3-a456-426614170101";
const ONTARIO_TRUST_CONTACT = "9c1e4567-e89b-12d3-a456-426614170102";

const SENDER_NAMES: Record<string, SenderInfo> = {
  [CURRENT_USER_ID]: { firstName: "John", lastName: "Doe" },
  [VEGA_PENSION_FUND_CONTACT]: { firstName: "Priya", lastName: "Nair" },
  [ONTARIO_TRUST_CONTACT]: { firstName: "Marcus", lastName: "Webb" },
};

const GP_TEAM: MessagingParticipant[] = [
  { userId: CURRENT_USER_ID, firstName: "John", lastName: "Doe" },
  { userId: "user-2", firstName: "Amelia", lastName: "Clarke" },
];

const LP_CONTACTS_BY_DEAL_OPPORTUNITY: Record<string, MessagingParticipant[]> =
  {
    [SERIES_A.dealOpportunityId]: [
      {
        userId: VEGA_PENSION_FUND_CONTACT,
        firstName: "Priya",
        lastName: "Nair",
      },
    ],
    [PROJECT_TITAN.dealOpportunityId]: [
      { userId: ONTARIO_TRUST_CONTACT, firstName: "Marcus", lastName: "Webb" },
    ],
  };

type ConversationSeed = {
  id: string;
  meta: ConversationResponse["meta"];
  unreadCount: number;
  messages: { senderId: string; sentAt: string; body: string }[];
};

const sideLetterHistory: ConversationSeed["messages"] = Array.from(
  { length: 21 },
  (_, index) => ({
    senderId: index % 2 === 0 ? ONTARIO_TRUST_CONTACT : CURRENT_USER_ID,
    sentAt: `2026-03-${String(index + 1).padStart(2, "0")}T09:00:00Z`,
    body:
      index % 2 === 0
        ? `Circulating side letter draft v${index + 1} — comments from our legal group are in the margin.`
        : `Reviewed draft v${index} — accepted the redlines except the reporting cadence; see our note inline.`,
  }),
);

const archivedConversationSeeds: ConversationSeed[] = Array.from(
  { length: 10 },
  (_, index) => {
    const isSeriesA = index % 2 === 0;

    return {
      id: `conversation-archived-${index + 1}`,
      meta: isSeriesA
        ? { ...SERIES_A, limitedPartnerName: "Vega Pension Fund" }
        : {
            ...PROJECT_TITAN,
            limitedPartnerName: "Ontario Teachers' Retirement Trust",
          },
      unreadCount: 0,
      messages: [
        {
          senderId: isSeriesA ? VEGA_PENSION_FUND_CONTACT : CURRENT_USER_ID,
          sentAt: `2026-02-${String(index + 1).padStart(2, "0")}T10:00:00Z`,
          body: `Q${(index % 4) + 1} follow-up on ${isSeriesA ? "the Series A round" : "Project Titan"} — resolved, keeping for the record.`,
        },
      ],
    };
  },
);

const conversationSeeds: ConversationSeed[] = [
  {
    id: "conversation-reconcile",
    meta: { ...SERIES_A, limitedPartnerName: "Vega Pension Fund" },
    unreadCount: 1,
    messages: [
      {
        senderId: VEGA_PENSION_FUND_CONTACT,
        sentAt: "2026-04-28T09:15:00Z",
        body: "Why do the numbers not reconcile?",
      },
    ],
  },
  {
    id: "conversation-fcf-figure",
    meta: { ...SERIES_A, limitedPartnerName: "Vega Pension Fund" },
    unreadCount: 1,
    messages: [
      {
        senderId: VEGA_PENSION_FUND_CONTACT,
        sentAt: "2026-04-28T08:40:00Z",
        body: "Why does the FCF figure on p. 298 not match the FDD report?",
      },
    ],
  },
  {
    id: "conversation-side-letter",
    meta: {
      ...PROJECT_TITAN,
      limitedPartnerName: "Ontario Teachers' Retirement Trust",
    },
    unreadCount: 2,
    messages: [
      ...sideLetterHistory,
      {
        senderId: ONTARIO_TRUST_CONTACT,
        sentAt: "2026-04-23T10:05:00Z",
        body: "Hi team – as we move toward final docs on Titan, our legal group flagged a few side letter items they'd like to discuss. Specifically: (1) MFN coverage across the Fund II LP base, (2) an excuse right for any portfolio company materially engaged in fossil fuel lending, and (3) quarterly fee transparency reporting beyond the standard ILPA template. Is there a time next week to walk through these before we paper the commitment?",
      },
      {
        senderId: CURRENT_USER_ID,
        sentAt: "2026-04-23T11:30:00Z",
        body: "Thanks for sending these through ahead of time – all three are workable in principle. On MFN, we can extend full coverage against all Fund II LPs at or below your commitment size, with the usual carve-outs for regulatory, tax, and sovereign-specific provisions. The fossil fuel exclusion we've granted before and can mirror the language from our Fund I precedent. Enhanced fee reporting is fine; we'd propose quarterly rather than monthly to keep it operationally sensible. How does Wednesday or Thursday afternoon UK time look for a call?",
      },
      {
        senderId: ONTARIO_TRUST_CONTACT,
        sentAt: "2026-04-23T14:10:00Z",
        body: "Thursday 2pm UK works well. One follow-up before the call: on the MFN carve-outs, could you share the list of sovereign-specific provisions that would be excluded? We had some back-and-forth on this in Fund I and want to make sure we're aligned early. Also, for the excuse right, our preference is a 30-day notice period rather than 45 – is that something you can accommodate?",
      },
      {
        senderId: ONTARIO_TRUST_CONTACT,
        sentAt: "2026-04-23T16:45:00Z",
        body: "Booked for Thursday 2pm UK – calendar invite coming separately. I'll send the sovereign carve-out list by EOD tomorrow; it's a short list, mostly around confidentiality obligations to other sovereign LPs and specific tax reporting structures. 30-day notice on the excuse right works for us.",
      },
    ],
  },
  {
    id: "conversation-model-reconcile",
    meta: { ...SERIES_A, limitedPartnerName: "Vega Pension Fund" },
    unreadCount: 1,
    messages: [
      {
        senderId: CURRENT_USER_ID,
        sentAt: "2026-07-02T09:15:00Z",
        body: "Do the numbers in the latest model reconcile with the FDD report?",
      },
      {
        senderId: VEGA_PENSION_FUND_CONTACT,
        sentAt: "2026-07-03T10:20:00Z",
        body: "They do",
      },
    ],
  },
  {
    id: "conversation-deadline",
    meta: { ...SERIES_A, limitedPartnerName: "Vega Pension Fund" },
    unreadCount: 1,
    messages: [
      {
        senderId: CURRENT_USER_ID,
        sentAt: "2026-04-13T16:30:00Z",
        body: "Would it be possible to extend the deadline for the commitment? Our investment committee meets next Friday.",
      },
      {
        senderId: VEGA_PENSION_FUND_CONTACT,
        sentAt: "2026-04-14T09:10:00Z",
        body: "The deadline is next Thursday - let us know if this works for your team, otherwise happy to jump on a call and arrange an alternate plan! Let us know how we can help!",
      },
    ],
  },
  {
    id: "conversation-allocation",
    meta: { ...SERIES_A, limitedPartnerName: "Vega Pension Fund" },
    unreadCount: 0,
    messages: [
      {
        senderId: VEGA_PENSION_FUND_CONTACT,
        sentAt: "2026-04-20T11:00:00Z",
        body: "Can you confirm our final allocation for the Series A round?",
      },
    ],
  },
  {
    id: "conversation-wire-details",
    meta: { ...SERIES_A, limitedPartnerName: "Vega Pension Fund" },
    unreadCount: 0,
    messages: [
      {
        senderId: CURRENT_USER_ID,
        sentAt: "2026-04-18T15:30:00Z",
        body: "Wire details for the capital call are in the data room under Funding.",
      },
    ],
  },
  {
    id: "conversation-celestial",
    meta: {
      ...PROJECT_TITAN,
      limitedPartnerName: "Ontario Teachers' Retirement Trust",
    },
    unreadCount: 0,
    messages: [
      {
        senderId: CURRENT_USER_ID,
        sentAt: "2026-04-14T08:05:00Z",
        body: "What does Celestial mean?",
      },
    ],
  },
  ...archivedConversationSeeds,
];

const buildMessage = ({
  conversationId,
  senderId,
  sentAt,
  body,
}: ConversationSeed["messages"][number] & {
  conversationId: string;
}): MessageResponse => ({
  id: crypto.randomUUID(),
  conversationId,
  senderId,
  senderType: SenderType.USER,
  contentType: MessageContentType.TEXT,
  messageContent: body,
  parentMessageId: null,
  createdAt: sentAt,
  editedAt: null,
  deletedAt: null,
});

const toMessageWithMeta = ({
  id,
  ...message
}: MessageResponse): MessageWithMetaResponse => ({
  ...message,
  messageId: id,
  isRead: true,
  senderInfo: SENDER_NAMES[message.senderId] ?? null,
});

const toLatestMessageSummary = (message: MessageResponse) => ({
  id: message.id,
  messageContent: message.messageContent,
  senderId: message.senderId,
  createdAt: message.createdAt,
});

const messagesByConversationId: Record<string, MessageResponse[]> =
  Object.fromEntries(
    conversationSeeds.map((seed) => [
      seed.id,
      seed.messages.map((message) =>
        buildMessage({ ...message, conversationId: seed.id }),
      ),
    ]),
  );

const conversations: ConversationResponse[] = conversationSeeds.map((seed) => {
  const messages = messagesByConversationId[seed.id];
  const firstMessage = messages[0];
  const lastMessage = messages[messages.length - 1];

  return {
    id: seed.id,
    productId: "coinvest-deals-app",
    title: null,
    status: ConversationStatus.HUMAN_HANDLING,
    createdBy: firstMessage.senderId,
    updatedBy: lastMessage.senderId,
    createdAt: firstMessage.createdAt,
    updatedAt: lastMessage.createdAt,
    meta: seed.meta,
    unreadCount: seed.unreadCount,
    latestMessage: toLatestMessageSummary(lastMessage),
    participants: [],
  };
});

const paginate = <Item>(items: Item[], page: number, pageSize: number) => ({
  data: items.slice((page - 1) * pageSize, page * pageSize),
  meta: {
    page,
    pageSize,
    count: items.length,
    totalPages: Math.ceil(items.length / pageSize),
    hasNextPage: page * pageSize < items.length,
  },
});

const findConversation = (id: string) =>
  conversations.find((conversation) => conversation.id === id) ??
  conversations[0];

export const messagingHandlers = [
  getListConversationsMockHandler(({ request }) => {
    const params = new URL(request.url).searchParams;
    const page = Number(params.get("page") ?? 1);
    const pageSize = Number(params.get("pageSize") ?? conversations.length);
    const metadataParam = params.get("metadata");
    const metaFilter: Record<string, string> =
      metadataParam === null ? {} : JSON.parse(metadataParam);

    const filtered = conversations
      .filter((conversation) =>
        Object.entries(metaFilter).every(
          ([key, value]) => conversation.meta[key] === value,
        ),
      )
      .sort((conversationA, conversationB) =>
        conversationB.updatedAt.localeCompare(conversationA.updatedAt),
      );

    return paginate(filtered, page, pageSize);
  }),
  // Registered before the /conversations/:id handler so the static
  // /conversations/unread-summary path isn't captured as an id.
  getGetConversationUnreadSummaryMockHandler(({ request }) => {
    const metaFilter = new URL(request.url).searchParams.getAll("metaFilter");
    const matched = conversations.filter((conversation) =>
      metaFilter.every((entry) => {
        const [key, value] = entry.split(":");

        return conversation.meta[key] === value;
      }),
    );

    return {
      data: {
        totalUnread: matched.reduce(
          (total, conversation) => total + conversation.unreadCount,
          0,
        ),
        metaGrouping: {},
        perConversation: [],
      },
    };
  }),
  getGetConversationMockHandler(({ params }) => ({
    data: findConversation(String(params.id)),
  })),
  getCreateConversationMockHandler(async ({ request }) => {
    const body = (await request.json()) as ConversationRequest;
    const conversationId = crypto.randomUUID();
    const now = new Date().toISOString();

    const meta = Object.fromEntries(
      Object.entries(body.meta).map(([key, value]) => [key, String(value)]),
    );

    const initialMessage = body.initialMessage
      ? buildMessage({
          conversationId,
          senderId: CURRENT_USER_ID,
          sentAt: now,
          body: body.initialMessage.messageContent,
        })
      : undefined;

    messagesByConversationId[conversationId] = initialMessage
      ? [initialMessage]
      : [];

    const conversation: ConversationResponse = {
      id: conversationId,
      productId: body.productId,
      title: null,
      status: ConversationStatus.HUMAN_HANDLING,
      createdBy: CURRENT_USER_ID,
      updatedBy: CURRENT_USER_ID,
      createdAt: now,
      updatedAt: now,
      meta,
      unreadCount: 0,
      latestMessage: initialMessage
        ? toLatestMessageSummary(initialMessage)
        : null,
      participants: [],
    };

    conversations.unshift(conversation);

    return { data: conversation };
  }),
  getListMessagesMockHandler(({ request }) => {
    const searchParams = new URL(request.url).searchParams;
    const conversationId = searchParams.get("conversationId") ?? "";
    const messages = messagesByConversationId[conversationId] ?? [];
    const page = Number(searchParams.get("page") ?? 1);
    const pageSize = Number(searchParams.get("pageSize") ?? messages.length);

    const sorted = [...messages].sort((messageA, messageB) =>
      messageA.createdAt.localeCompare(messageB.createdAt),
    );

    if (searchParams.get("sort")?.endsWith(":desc") === true) {
      sorted.reverse();
    }

    return paginate(sorted.map(toMessageWithMeta), page, pageSize);
  }),
  getSendMessageMockHandler(async ({ params, request }) => {
    const body = (await request.json()) as MessageRequest;
    const conversationId = String(params.conversationId);
    const message = buildMessage({
      conversationId,
      senderId: CURRENT_USER_ID,
      sentAt: new Date().toISOString(),
      body: body.messageContent,
    });

    messagesByConversationId[conversationId] = [
      ...(messagesByConversationId[conversationId] ?? []),
      message,
    ];

    const conversation = findConversation(conversationId);
    conversation.latestMessage = toLatestMessageSummary(message);
    conversation.updatedAt = message.createdAt;
    conversation.updatedBy = message.senderId;

    return { data: message };
  }),
  getMarkConversationReadMockHandler(({ params }) => {
    findConversation(String(params.id)).unreadCount = 0;
  }),
  // Contract enforces a 2-char minimum searchQuery; the mock returns the full
  // list when it's absent because conversation creation auto-attaches everyone.
  getSearchMessagingParticipantsMockHandler(({ params, request }) => {
    const searchQuery =
      new URL(request.url).searchParams.get("searchQuery")?.toLowerCase() ?? "";
    const matchesQuery = (participant: MessagingParticipant) =>
      `${participant.firstName} ${participant.lastName}`
        .toLowerCase()
        .includes(searchQuery);
    const lpUsers =
      LP_CONTACTS_BY_DEAL_OPPORTUNITY[String(params.dealOpportunityId)] ?? [];

    return {
      data: {
        gpUsers: GP_TEAM.filter(matchesQuery),
        lpUsers: lpUsers.filter(matchesQuery),
      },
    };
  }),
];
